import type { JSX, MouseEvent } from 'react';
import { TableRow } from '@/components/ui/table';
import { isInteractiveTarget } from './rowClick';

interface ClickableTableRowProps {
  onActivate: () => void;
  label: string;
  className?: string;
  testId?: string;
  children: JSX.Element | JSX.Element[];
}

/**
 * Table row that opens the entity when the user clicks anywhere in the row, not only on
 * the entity name. Interactive elements (buttons, links, selects, menu triggers) keep
 * their own behaviour; the actions menu is ignored so dialogs do not also navigate.
 */
export function ClickableTableRow({
  onActivate,
  label,
  className,
  testId,
  children,
}: ClickableTableRowProps): JSX.Element {
  const handleClick = (event: MouseEvent<HTMLTableRowElement>): void => {
    if (isInteractiveTarget(event.target, event.currentTarget)) return;
    onActivate();
  };

  const rowClassName = className ? `cursor-pointer ${className}` : 'cursor-pointer';

  return (
    <TableRow
      className={rowClassName}
      onClick={handleClick}
      data-testid={testId ?? 'clickable-row'}
      aria-label={label}
    >
      {children}
    </TableRow>
  );
}
